import type { AgentPage } from '../../types/negotiationAgents';
import { readAgentResponse } from '../../utils/negotiationAgents/apiResponse';
import { withSandboxWorkspace } from '../../utils/flows/sandboxUrl';
import { apiDelete, apiGet, apiPost, apiPut } from '@/shared/utils/requestBuilder';
import { parseApiResponse } from '@/shared/utils/apiResponse';
import {
  CreateFlowInput,
  Flow,
  UpdateFlowInput,
  type AiModelInfo,
  type LinhaDeEnvio,
  type CreateNegociacaoSituacaoInput,
  type NegociacaoSituacaoComEscritorios,
  type UpdateSituacaoEscritoriosInput,
  type SandboxMessage,
  type SandboxReplyResult,
  type SandboxStartResult,
  type WhatsappTemplateDetails,
  type ZapsignTemplateInfo,
} from '../../types/flows';

const BASE_URL = '/admin/flows';
const AI_URL = '/ai';
const TEMPLATES_URL = '/extrajudicial/templates';
const ZAPSIGN_URL = '/flows';

export const flowsService = {
  async listFlows(params?: {
    type?: string;
    scenarioType?: string;
    status?: string;
    escritorioId?: string;
  }): Promise<Flow[]> {
    const queryParams = new URLSearchParams();
    if (params?.type) queryParams.append('type', params.type);
    if (params?.scenarioType) queryParams.append('scenarioType', params.scenarioType);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.escritorioId) queryParams.append('escritorioId', params.escritorioId);

    const query = queryParams.toString();
    const url = query ? `${BASE_URL}?${query}` : BASE_URL;
    const response = await apiGet(url);
    return parseApiResponse<Flow[]>(response);
  },

  async getFlow(flowId: string): Promise<Flow> {
    const response = await apiGet(`${BASE_URL}/${flowId}`);
    return parseApiResponse<Flow>(response);
  },

  async createFlow(data: CreateFlowInput): Promise<Flow> {
    const response = await apiPost(BASE_URL, data);
    return parseApiResponse<Flow>(response);
  },

  async updateFlow(flowId: string, data: UpdateFlowInput): Promise<Flow> {
    const response = await apiPut(`${BASE_URL}/${flowId}`, data);
    return readAgentResponse<Flow>(response);
  },

  async listLinhasDeEnvio(): Promise<LinhaDeEnvio[]> {
    const response = await apiGet('/admin/templates/linhas');
    // `parseApiResponse` já devolve o `data` de dentro do envelope `{ success, data }` — extrair
    // de novo daria `undefined`, que o reducer gravaria no estado e derrubaria o seletor.
    return await parseApiResponse<LinhaDeEnvio[]>(response);
  },

  async getWhatsappTemplateById(templateId: string): Promise<WhatsappTemplateDetails> {
    const response = await apiGet(`${TEMPLATES_URL}/${templateId}`);
    return parseApiResponse<WhatsappTemplateDetails>(response);
  },

  async sandboxStart(
    flowId: string,
    variables: Record<string, unknown>,
    escritorioId: string,
  ): Promise<SandboxStartResult> {
    const response = await apiPost(
      withSandboxWorkspace(`${BASE_URL}/${flowId}/sandbox/start`, escritorioId),
      { variables, escritorioId },
    );
    return parseApiResponse<SandboxStartResult>(response);
  },

  async sandboxReply(
    flowId: string,
    conversaId: string,
    message: string,
    escritorioId?: string,
  ): Promise<SandboxReplyResult> {
    const response = await apiPost(
      withSandboxWorkspace(`${BASE_URL}/${flowId}/sandbox/${conversaId}/reply`, escritorioId),
      {
        message,
        ...(escritorioId ? { escritorioId } : {}),
      },
    );
    return parseApiResponse<SandboxReplyResult>(response);
  },

  async sandboxMessages(
    flowId: string,
    conversaId: string,
    page = 1,
    escritorioId?: string,
  ): Promise<AgentPage<SandboxMessage>> {
    const response = await apiGet(
      withSandboxWorkspace(
        `${BASE_URL}/${flowId}/sandbox/${conversaId}/messages?page=${page}&limit=20`,
        escritorioId,
      ),
    );
    return parseApiResponse<AgentPage<SandboxMessage>>(response);
  },

  async sandboxCleanup(flowId: string, conversaId: string, escritorioId?: string): Promise<void> {
    const response = await apiDelete(
      withSandboxWorkspace(`${BASE_URL}/${flowId}/sandbox/${conversaId}`, escritorioId),
    );
    if (!response.ok) await readAgentResponse(response);
  },

  async generateMultiAgentConfig(
    description: string,
    currentConfig?: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    const response = await apiPost('/ia/multi-agent/generate', { description, currentConfig });
    return parseApiResponse<Record<string, unknown>>(response);
  },

  async listAiModels(): Promise<AiModelInfo[]> {
    const response = await apiGet(`${AI_URL}/models`);
    return parseApiResponse<AiModelInfo[]>(response);
  },

  async createNegociacaoSituacao(
    payload: CreateNegociacaoSituacaoInput,
  ): Promise<NegociacaoSituacaoComEscritorios> {
    const response = await apiPost('/negociacao/situacoes', payload);
    return parseApiResponse<NegociacaoSituacaoComEscritorios>(response);
  },

  async updateSituacaoEscritorios(
    payload: UpdateSituacaoEscritoriosInput,
  ): Promise<NegociacaoSituacaoComEscritorios> {
    const response = await apiPut(`/negociacao/situacoes/${payload.situacaoId}/escritorios`, {
      escritorioIds: payload.escritorioIds,
    });
    return parseApiResponse<NegociacaoSituacaoComEscritorios>(response);
  },

  async getZapsignTemplateInputs(templateId: string): Promise<ZapsignTemplateInfo> {
    const response = await apiGet(`${ZAPSIGN_URL}/zapsign/templates/${templateId}`);
    return parseApiResponse<ZapsignTemplateInfo>(response);
  },
};
